import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ringGroups } from '@/api/ringGroups';
import type { RingGroupRow, CreateGroupBody, RingStrategy, MemberRow } from '@/api/ringGroups';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { formatRef } from '@/lib/formatRef';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Trash2, Phone, PhoneOutgoing, Users, UserSquare } from 'lucide-react';
import { ContactPicker } from '@/components/ContactPicker';
import { contacts as contactsApi } from '@/api/contacts';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

const STRATEGY_LABELS: Record<RingStrategy, string> = {
  simultaneous: 'Ring all at once',
  sequential: 'One at a time (in order)',
  round_robin: 'Round robin',
  random: 'Random',
};

export function RingGroupsList() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const canMutate = CAN_MUTATE.includes(user.role);
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RingGroupRow | null>(null);
  const [membersTarget, setMembersTarget] = useState<RingGroupRow | null>(null);
  const [cidTarget, setCidTarget] = useState<RingGroupRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['ringGroups', effectiveCustomerId],
    queryFn: () => ringGroups.list({ limit: 200, customer_id: effectiveCustomerId ?? undefined }),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => ringGroups.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ringGroups'] }); setDeleteTarget(null); },
  });

  const columns = [
    { header: 'Ref', cell: (r: RingGroupRow) => <span className="font-mono text-xs text-slate-500">{formatRef(r.ref_number, 'RG')}</span> },
    { header: 'Name', cell: (r: RingGroupRow) => <span className="text-sm font-medium">{r.name}</span> },
    { header: 'Strategy', cell: (r: RingGroupRow) => (
      <Badge variant="outline" className="text-[10px]">{STRATEGY_LABELS[r.strategy]}</Badge>
    ) },
    { header: 'Timeout', cell: (r: RingGroupRow) => (
      <span className="text-xs text-slate-500 tabular-nums">{r.ring_timeout_sec}s</span>
    ) },
    { header: 'Members', cell: (r: RingGroupRow) => (
      <button
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        onClick={(e) => { e.stopPropagation(); setMembersTarget(r); }}
      >
        <Users className="h-3.5 w-3.5" />
        {r.member_count}
      </button>
    ) },
    { header: 'Internal CID', cell: (r: RingGroupRow) => <RgCidCell row={r} /> },
    { header: 'Active', cell: (r: RingGroupRow) => (
      r.active ? <Badge variant="default">Active</Badge> : <Badge variant="secondary">Inactive</Badge>
    ) },
    { header: '', className: 'w-10 text-end', cell: (r: RingGroupRow) => (
      canMutate ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label="Row actions" onClick={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={() => setMembersTarget(r)}>
              <Users className="h-3.5 w-3.5 mr-2" /> Manage members
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setCidTarget(r)}>
              <UserSquare className="h-3.5 w-3.5 mr-2" /> Set internal CID
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
              onClick={() => setDeleteTarget(r)}>
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null
    ) },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Telephony"
        title="Ring groups"
        description="Route an inbound call to multiple phones / extensions at once or in sequence. Hook into your IVR tree via a ring_group node pointing at the group's id."
        actions={canMutate && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> New group
          </Button>
        )}
      />

      {isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (
        <DataTable rows={rows} columns={columns} empty="No ring groups yet. Create one to start routing inbound calls to multiple destinations." />
      )}

      {createOpen && <CreateDialog onClose={() => setCreateOpen(false)} user={user} />}
      {deleteTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Delete ring group?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">
              <span className="font-medium">{deleteTarget.name}</span> will be soft-deleted.
              Any IVR ring_group node pointing at it will no_answer-fall-back on next call.
            </p>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" disabled={remove.isPending}>Cancel</Button></DialogClose>
              <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate(deleteTarget.id)}>
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {membersTarget && <MembersDialog group={membersTarget} onClose={() => setMembersTarget(null)} canMutate={canMutate} />}
      {cidTarget && (
        <RgSetCidDialog
          group={cidTarget}
          onClose={() => setCidTarget(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['ringGroups'] });
            setCidTarget(null);
          }}
        />
      )}
    </>
  );
}

/**
 * Inline cell — same role as ExtensionsList's CidCell. Shows the
 * resolved contact (or override) at a glance.
 */
function RgCidCell({ row }: { row: RingGroupRow }) {
  const customerId = row.customer_id;
  const { data } = useQuery({
    queryKey: ['contacts', 'picker', customerId],
    queryFn: () => contactsApi.list({ customer_id: customerId, active_only: true, limit: 500 }),
    enabled: !!row.internal_cid_contact_id,
  });
  if (row.internal_cid_contact_id) {
    const contact = data?.rows.find((c) => c.id === row.internal_cid_contact_id);
    if (contact) {
      return (
        <span className="text-xs">
          <span className="font-medium">{contact.display_name}</span>
          {contact.internal_cid_phone && (
            <span className="font-mono text-muted-foreground"> · {contact.internal_cid_phone}</span>
          )}
        </span>
      );
    }
    return <span className="text-xs text-slate-400">…</span>;
  }
  if (row.internal_cid_override) {
    return <span className="text-xs italic">{row.internal_cid_override}</span>;
  }
  return <span className="text-xs text-slate-400">—</span>;
}

/** Set / clear a ring group's internal CID. Same shape as the
 *  ExtensionsList dialog but calls `ringGroups.update` instead. */
function RgSetCidDialog({
  group, onClose, onSaved,
}: {
  group: RingGroupRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [contactId, setContactId] = useState<string | null>(group.internal_cid_contact_id);
  const [override, setOverride] = useState(group.internal_cid_override ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => ringGroups.update(group.id, {
      internal_cid_contact_id: contactId,
      internal_cid_override: override.trim() || null,
    }),
    onSuccess: () => onSaved(),
    onError: (err) => setError(err instanceof Error ? err.message : 'Save failed.'),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !save.isPending) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserSquare className="h-4 w-4" />
            Internal CID for {group.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-xs text-muted-foreground leading-snug">
            Stamped on every leg of the fan-out so each member of the
            ring group sees the same directory entry on their handset.
            Pick a contact (preferred) or type a freeform name. If both
            are blank, the caller's original external CID flows through.
          </p>
          <ContactPicker
            label="Linked contact"
            value={contactId}
            onChange={setContactId}
          />
          <div className="space-y-1.5">
            <Label htmlFor="rg-cid-override">Freeform override</Label>
            <Input
              id="rg-cid-override"
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              placeholder="e.g. Support, Sales"
              maxLength={200}
            />
            <p className="text-[11px] text-muted-foreground">
              Used only when no contact is linked above.
            </p>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={save.isPending}>Cancel</Button>
          </DialogClose>
          <Button onClick={() => { setError(null); save.mutate(); }} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ onClose, user: _user }: { onClose: () => void; user: ReturnType<typeof useCurrentUser> }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [strategy, setStrategy] = useState<RingStrategy>('simultaneous');
  const [timeout, setTimeout] = useState(20);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  const create = useMutation({
    mutationFn: (body: CreateGroupBody) => ringGroups.create(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ringGroups'] }); onClose(); },
    onError: (err) => setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  function submit() {
    setError(null);
    if (!name.trim()) { setError('Name required.'); return; }
    if (effectiveCustomerId === null) { setError('Customer required.'); return; }
    create.mutate({
      name: name.trim(),
      strategy,
      ring_timeout_sec: timeout,
      ...(effectiveCustomerId
        ? { customer_id: effectiveCustomerId }
        : {}),
    });
  }

  const canSubmit = name.trim().length > 0 && effectiveCustomerId !== null;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New ring group</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {customerPicker}
          <CustomerScopeChip />
          <div className="space-y-1">
            <Label htmlFor="g-name">Name</Label>
            <Input id="g-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sales team" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Strategy</Label>
              <Select value={strategy} onValueChange={(v) => setStrategy(v as RingStrategy)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="simultaneous">Simultaneous</SelectItem>
                  <SelectItem value="sequential">Sequential</SelectItem>
                  <SelectItem value="round_robin">Round robin</SelectItem>
                  <SelectItem value="random">Random</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="g-timeout">Ring timeout (sec)</Label>
              <Input id="g-timeout" type="number" min={5} max={300} value={timeout}
                onChange={(e) => setTimeout(parseInt(e.target.value, 10) || 20)} />
            </div>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline" disabled={create.isPending}>Cancel</Button></DialogClose>
          <Button type="button" onClick={submit} disabled={create.isPending || !canSubmit}>
            {create.isPending ? 'Creating…' : 'Create group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembersDialog({ group, onClose, canMutate }: { group: RingGroupRow; onClose: () => void; canMutate: boolean }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<'extension' | 'phone'>('extension');
  const [value, setValue] = useState('');
  const [order, setOrder] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['ringGroupMembers', group.id],
    queryFn: () => ringGroups.listMembers(group.id),
  });
  const members = data?.rows ?? [];

  const add = useMutation({
    mutationFn: () => ringGroups.addMember(group.id, {
      member_kind: kind, member_value: value.trim(), ring_order: order,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ringGroupMembers', group.id] });
      qc.invalidateQueries({ queryKey: ['ringGroups'] });
      setValue(''); setOrder(members.length + 1); setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Add failed.'),
  });
  const remove = useMutation({
    mutationFn: (memberId: string) => ringGroups.removeMember(group.id, memberId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ringGroupMembers', group.id] });
      qc.invalidateQueries({ queryKey: ['ringGroups'] });
    },
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            Members of {group.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {members.length === 0 ? (
            <Card><CardContent className="py-5 text-center text-sm text-muted-foreground">
              No members yet. Add at least one to start routing calls.
            </CardContent></Card>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
              {members.map((m) => <MemberRow key={m.id} member={m} canMutate={canMutate}
                onRemove={() => remove.mutate(m.id)} removing={remove.isPending} />)}
            </ul>
          )}

          {canMutate && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Add member</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid gap-2 grid-cols-[auto_1fr_6rem]">
                  <Select value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="extension">Extension</SelectItem>
                      <SelectItem value="phone">Phone (E.164)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input value={value} onChange={(e) => setValue(e.target.value)}
                    placeholder={kind === 'extension' ? '1001' : '+18005551234'}
                    className="font-mono text-sm" />
                  <Input type="number" min={0} max={999} value={order}
                    onChange={(e) => setOrder(parseInt(e.target.value, 10) || 0)}
                    className="font-mono text-sm" placeholder="order" />
                </div>
                {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
                <div className="flex justify-end">
                  <Button size="sm" onClick={() => add.mutate()} disabled={add.isPending || !value.trim()}>
                    {add.isPending ? 'Adding…' : 'Add member'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline">Close</Button></DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MemberRow({
  member, canMutate, onRemove, removing,
}: { member: MemberRow; canMutate: boolean; onRemove: () => void; removing: boolean }) {
  return (
    <li className="flex items-center justify-between gap-3 px-4 py-2.5">
      <div className="flex items-center gap-2 min-w-0">
        {member.member_kind === 'phone'
          ? <PhoneOutgoing className="h-3.5 w-3.5 text-muted-foreground" />
          : <Phone className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className="font-mono text-sm">{member.member_value}</span>
        <Badge variant="outline" className="text-[10px]">{member.member_kind}</Badge>
        <span className="text-xs text-muted-foreground ml-2">order {member.ring_order}</span>
      </div>
      {canMutate && (
        <Button size="sm" variant="ghost" onClick={onRemove} disabled={removing}>
          <Trash2 className="h-3.5 w-3.5 text-slate-400" />
        </Button>
      )}
    </li>
  );
}
